<script>
  import { onMount } from 'svelte';
  let videos = [];
  let search = '';
  let pageFilter = '';
  let currentVideo = null;

  onMount(async () => {
    const res = await fetch('/videos.json');
    videos = await res.json();
  });

  $: pages = [...new Set(videos.map(v => v.page))];
  $: filtered = videos.filter(v => 
    (!pageFilter || v.page === pageFilter) && 
    v.event.toLowerCase().includes(search.toLowerCase())
  );

  function openVideo(video) {
    currentVideo = video;
  }

  function closeVideo() {
    currentVideo = null;
  }

  function extractLinks(text) {
    if (!text || text.includes('No code') || text.includes('No slides') || !text.includes('[')) {
      return [];
    }
    
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const links = [];
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      links.push({
        text: match[1],
        url: match[2]
      });
    }
    
    return links;
  }

  function getThumbnail(youtubeId) {
    return `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;
  }
</script>

<div class="app">
  {#if !currentVideo}
    <!-- Browse View -->
    <header>
      <h1>🚀 Awesome AIM Index</h1>
      <div class="search-controls">
        <input 
          type="text" 
          placeholder="🔍 Search videos..." 
          bind:value={search}
          class="search-input"
        >
        <select bind:value={pageFilter} class="filter-select">
          <option value="">All Sources</option>
          {#each pages as page}
            <option value={page}>{page}</option>
          {/each}
        </select>
      </div>
    </header>

    <main class="grid-container">
      <div class="video-grid">
        {#each filtered as video}
          <div class="video-card" on:click={() => openVideo(video)}>
            <div class="video-thumbnail">
              <img src={getThumbnail(video.youtubeId)} alt={video.event} />
              <div class="play-overlay">
                <div class="play-button">▶</div>
              </div>
            </div>
            <div class="video-info">
              <h3 class="video-title">{video.event}</h3>
              <p class="video-meta">
                <span class="date">{video.date}</span>
                <span class="source">{video.page}</span>
              </p>
            </div>
          </div>
        {/each}
      </div>
    </main>
  {:else}
    <!-- Player View -->
    <div class="player-view">
      <header class="player-header">
        <button class="back-button" on:click={closeVideo}>← Back to Browse</button>
        <h1>{currentVideo.event}</h1>
      </header>

      <div class="player-content">
        <div class="video-section">
          <div class="video-player">
            <iframe 
              src={`https://www.youtube.com/embed/${currentVideo.youtubeId}?autoplay=1`}
              title={currentVideo.event}
              frameborder="0" 
              allowfullscreen
            ></iframe>
          </div>
        </div>

        <div class="resources-section">
          <h2>📚 Resources</h2>
          
          <div class="resource-group">
            <h3>🎥 Video</h3>
            <p class="resource-meta">{currentVideo.date} • {currentVideo.page}</p>
          </div>

          {#if currentVideo.code && !currentVideo.code.includes('No code')}
            <div class="resource-group">
              <h3>💻 Code & Notebooks</h3>
              <div class="resource-links">
                {#each extractLinks(currentVideo.code) as link}
                  <a href={link.url} target="_blank" rel="noopener noreferrer" class="resource-link">
                    {link.text}
                  </a>
                {/each}
              </div>
            </div>
          {/if}

          {#if currentVideo.slides && !currentVideo.slides.includes('No slides') && currentVideo.slides.trim()}
            <div class="resource-group">
              <h3>📊 Slides</h3>
              <div class="resource-links">
                {#each extractLinks(currentVideo.slides) as link}
                  <a href={link.url} target="_blank" rel="noopener noreferrer" class="resource-link">
                    {link.text}
                  </a>
                {/each}
              </div>
            </div>
          {/if}

          <div class="resource-group">
            <h3>🔗 Original Video</h3>
            <div class="resource-links">
              {#each extractLinks(currentVideo.video) as link}
                <a href={link.url} target="_blank" rel="noopener noreferrer" class="resource-link">
                  {link.text}
                </a>
              {/each}
            </div>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  :global(body) {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    background: linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%);
    color: #ffffff;
    line-height: 1.6;
  }

  .app {
    min-height: 100vh;
  }

  /* Browse View Styles */
  header {
    background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%);
    padding: 2rem;
    text-align: center;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  h1 {
    margin: 0 0 1.5rem 0;
    font-size: 2.5rem;
    font-weight: 700;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  }

  .search-controls {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
    max-width: 600px;
    margin: 0 auto;
  }

  .search-input, .filter-select {
    padding: 0.75rem 1rem;
    border: none;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    font-size: 1rem;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }

  .search-input {
    flex: 1;
    min-width: 300px;
  }

  .search-input::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  .search-input:focus, .filter-select:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 255, 255, 0.1);
  }

  .grid-container {
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;
  }

  .video-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 2rem;
  }

  .video-card {
    background: linear-gradient(145deg, #1e1e1e 0%, #2a2a2a 100%);
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  .video-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(99, 102, 241, 0.3);
  }

  .video-thumbnail {
    position: relative;
    aspect-ratio: 1;
    overflow: hidden;
  }

  .video-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .video-card:hover .video-thumbnail img {
    transform: scale(1.05);
  }

  .play-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .video-card:hover .play-overlay {
    opacity: 1;
  }

  .play-button {
    width: 60px;
    height: 60px;
    background: rgba(99, 102, 241, 0.9);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
    backdrop-filter: blur(10px);
  }

  .video-info {
    padding: 1.5rem;
  }

  .video-title {
    margin: 0 0 0.5rem 0;
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .video-meta {
    margin: 0;
    display: flex;
    justify-content: space-between;
    font-size: 0.9rem;
    color: #a0a0a0;
  }

  .date {
    color: #06b6d4;
  }

  .source {
    color: #8b5cf6;
    font-weight: 500;
  }

  /* Player View Styles */
  .player-view {
    min-height: 100vh;
  }

  .player-header {
    background: linear-gradient(90deg, #1e1e1e 0%, #2a2a2a 100%);
    padding: 1.5rem 2rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  }

  .back-button {
    background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.3s ease;
  }

  .back-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
  }

  .player-header h1 {
    margin: 0;
    font-size: 1.5rem;
    flex: 1;
  }

  .player-content {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 2rem;
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;
  }

  .video-player {
    aspect-ratio: 16/9;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  }

  .video-player iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  .resources-section {
    background: linear-gradient(145deg, #1e1e1e 0%, #2a2a2a 100%);
    border-radius: 16px;
    padding: 2rem;
    height: fit-content;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  .resources-section h2 {
    margin: 0 0 1.5rem 0;
    color: #06b6d4;
    font-size: 1.5rem;
  }

  .resource-group {
    margin-bottom: 2rem;
  }

  .resource-group h3 {
    margin: 0 0 0.5rem 0;
    color: #8b5cf6;
    font-size: 1.1rem;
  }

  .resource-meta {
    margin: 0 0 1rem 0;
    color: #a0a0a0;
    font-size: 0.9rem;
  }

  .resource-links {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .resource-link {
    color: #06b6d4;
    text-decoration: none;
    padding: 0.5rem 0.75rem;
    background: rgba(6, 182, 212, 0.1);
    border-radius: 8px;
    transition: all 0.3s ease;
    font-size: 0.9rem;
  }

  .resource-link:hover {
    background: rgba(6, 182, 212, 0.2);
    transform: translateX(4px);
  }

  @media (max-width: 768px) {
    .player-content {
      grid-template-columns: 1fr;
    }
    
    .video-grid {
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1rem;
    }
    
    header {
      padding: 1rem;
    }
    
    h1 {
      font-size: 2rem;
    }
    
    .search-controls {
      flex-direction: column;
      align-items: center;
    }
    
    .search-input {
      min-width: 100%;
    }
  }
</style>
